import React, { useEffect, useMemo, useState } from "react";
import {
  getMerchantAppBranding,
  MerchantPlatform,
} from "./merchantBranding";
import {
  GetStartedPanel,
  getStartedTips,
  markGetStartedComplete,
} from "./merchantGetStartedState";
import "./MerchantGetStarted.css";

interface MerchantGetStartedProps {
  platform: MerchantPlatform;
  shop: string;
  storeName?: string;
  open: boolean;
  onClose: () => void;
  onNavigate?: (panel: GetStartedPanel) => void;
}

const MerchantGetStarted: React.FC<MerchantGetStartedProps> = ({
  platform,
  shop,
  storeName,
  open,
  onClose,
  onNavigate,
}) => {
  const branding = getMerchantAppBranding(platform);
  const tips = useMemo(() => getStartedTips(platform), [platform]);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (open) {
      setStep(0);
    }
  }, [open]);

  const dismiss = () => {
    markGetStartedComplete(platform, shop);
    onClose();
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      markGetStartedComplete(platform, shop);
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, platform, shop, onClose]);

  if (!open) {
    return null;
  }

  const current = tips[step] || tips[0];
  const isLast = step >= tips.length - 1;
  const progress = ((step + 1) / tips.length) * 100;

  const goNext = () => {
    if (isLast) {
      markGetStartedComplete(platform, shop);
      if (current.panel && onNavigate) {
        onNavigate(current.panel);
      } else {
        onClose();
      }
      return;
    }
    setStep((value) => Math.min(value + 1, tips.length - 1));
  };

  const goBack = () => {
    setStep((value) => Math.max(value - 1, 0));
  };

  const jumpTo = (index: number) => {
    setStep(index);
  };

  const useTipCta = () => {
    if (isLast) {
      markGetStartedComplete(platform, shop);
    }
    if (current.panel && onNavigate) {
      onNavigate(current.panel);
    }
  };

  return (
    <div className="zulu-gs" role="dialog" aria-modal="true" aria-labelledby="zulu-gs-title">
      <button
        type="button"
        className="zulu-gs__backdrop"
        aria-label="Close get started tips"
        onClick={dismiss}
      />
      <div className="zulu-gs__card">
        <header className="zulu-gs__head">
          <div>
            <p className="zulu-gs__eyebrow">Get started · {branding.label}</p>
            <h2 id="zulu-gs-title">
              {storeName ? `${storeName}` : "Your merchant portal"}
            </h2>
          </div>
          <button
            type="button"
            className="zulu-gs__close"
            onClick={dismiss}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="zulu-gs__progress" aria-hidden="true">
          <span style={{ width: `${progress}%` }} />
        </div>

        <div className="zulu-gs__body">
          <aside className="zulu-gs__rail" aria-label="Tour steps">
            {tips.map((tip, index) => (
              <button
                key={tip.id}
                type="button"
                className={`zulu-gs__rail-item${
                  index === step ? " is-active" : ""
                }${index < step ? " is-done" : ""}`}
                onClick={() => jumpTo(index)}
              >
                <span className="zulu-gs__rail-num">{index + 1}</span>
                <span className="zulu-gs__rail-copy">
                  <strong>{tip.title}</strong>
                  <small>{tip.subtitle}</small>
                </span>
              </button>
            ))}
          </aside>

          <div className="zulu-gs__main" key={current.id}>
            <div className="zulu-gs__badge" aria-hidden="true">
              <TipIcon id={current.id} />
            </div>
            <p className="zulu-gs__step-label">
              Step {step + 1} of {tips.length}
            </p>
            <h3>{current.title}</h3>
            <p className="zulu-gs__subtitle">{current.subtitle}</p>
            {current.body.map((paragraph) => (
              <p key={paragraph} className="zulu-gs__copy">
                {paragraph}
              </p>
            ))}
            <ul className="zulu-gs__highlights">
              {current.highlights.map((item) => (
                <li key={item}>
                  <span aria-hidden="true">✓</span>
                  {item}
                </li>
              ))}
            </ul>
            {current.ctaLabel && current.panel && onNavigate ? (
              <button
                type="button"
                className="zulu-gs__link-btn"
                onClick={useTipCta}
              >
                {current.ctaLabel}
              </button>
            ) : null}
          </div>
        </div>

        <footer className="zulu-gs__foot">
          <button type="button" className="zulu-gs__text-btn" onClick={dismiss}>
            Skip tour
          </button>
          <div className="zulu-gs__actions">
            <button
              type="button"
              className="zulu-gs__secondary"
              onClick={goBack}
              disabled={step === 0}
            >
              Back
            </button>
            <button type="button" className="zulu-gs__primary" onClick={goNext}>
              {isLast ? "Finish & open dashboard" : "Next"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

const TipIcon: React.FC<{ id: string }> = ({ id }) => {
  switch (id) {
    case "dashboard":
      return <IconGrid />;
    case "orders":
      return <IconList />;
    case "notifications":
      return <IconBell />;
    case "settings":
      return <IconGear />;
    case "platform":
      return <IconPlug />;
    case "done":
      return <IconCheck />;
    default:
      return <IconSpark />;
  }
};

const IconSpark = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M12 3l1.6 5.2L19 10l-5.4 1.8L12 17l-1.6-5.2L5 10l5.4-1.8L12 3z" />
  </svg>
);

const IconGrid = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </svg>
);

const IconList = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
  </svg>
);

const IconBell = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M6 17h12l-1-7a5 5 0 10-10 0l-1 7z" />
    <path d="M10 17a2 2 0 004 0" />
  </svg>
);

const IconGear = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3v2M12 19v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M3 12h2M19 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);

const IconPlug = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M9 7V3M15 7V3M8 7h8v4a4 4 0 01-4 4h0a4 4 0 01-4-4V7z" />
    <path d="M12 15v6" />
  </svg>
);

const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12l5 5L20 7" />
  </svg>
);

export default MerchantGetStarted;
